<script>
export default {
  name: 'VideoPlayerModal',
  props: {
    src: {
      type: String,
      required: true,
    },
    authorDisplay: {
      type: String,
      default: '',
    },
    authorPictureUrl: {
      type: String,
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 1,
      isMuted: false,
      isDragging: false,
      wasPlayingBeforeDrag: false,
    };
  },
  computed: {
    progressPercent() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    formattedDuration() {
      return this.formatTime(this.duration);
    },
  },
  mounted() {
    this.$refs.video.play();
    this._onKeydown = (e) => {
      if (e.key === 'Escape') this.$emit('close');
      if (e.code === 'Space') {
        e.preventDefault();
        this.togglePlay();
      }
    };
    document.addEventListener('keydown', this._onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },
  methods: {
    formatTime(seconds) {
      const total = Math.floor(seconds || 0);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    togglePlay() {
      if (this.$refs.video.paused) {
        this.$refs.video.play();
      } else {
        this.$refs.video.pause();
      }
    },
    onPlay() {
      this.isPlaying = true;
    },
    onPause() {
      this.isPlaying = false;
    },
    onLoadedMetadata() {
      this.duration = this.$refs.video.duration;
    },
    onTimeUpdate() {
      if (!this.isDragging && this.$refs.video) this.currentTime = this.$refs.video.currentTime;
    },
    timeAtClientX(clientX) {
      const rect = this.$refs.seekBar.getBoundingClientRect();
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      return ratio * this.duration;
    },
    startDrag(e) {
      this.isDragging = true;
      this.wasPlayingBeforeDrag = !this.$refs.video.paused;
      this.$refs.video.pause();
      this.currentTime = this.timeAtClientX(e.clientX);
      this.$refs.video.currentTime = this.currentTime;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      this.currentTime = this.timeAtClientX(e.clientX);
      this._pendingSeekTime = this.currentTime;
      if (this._seekFrameQueued) return;
      this._seekFrameQueued = true;
      requestAnimationFrame(() => {
        this._seekFrameQueued = false;
        this.$refs.video.currentTime = this._pendingSeekTime;
      });
    },
    stopDrag(e) {
      this.isDragging = false;
      this.currentTime = this.timeAtClientX(e.clientX);
      this.$refs.video.currentTime = this.currentTime;
      if (this.wasPlayingBeforeDrag) this.$refs.video.play();
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    skip(seconds) {
      this.$refs.video.currentTime = Math.min(Math.max(this.$refs.video.currentTime + seconds, 0), this.duration);
    },
    toggleMute() {
      this.$refs.video.muted = !this.$refs.video.muted;
      this.isMuted = this.$refs.video.muted;
    },
    async download() {
      const response = await fetch(this.src);
      const blob = await response.blob();
      const extension = blob.type.split('/')[1] ?? 'mp4';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `video.${extension}`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<template>
  <div class="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col" @click.self="$emit('close')">
    <div class="flex items-center justify-between p-4">
      <div class="flex items-center gap-2">
        <img
          v-if="authorPictureUrl"
          :src="authorPictureUrl"
          :alt="authorDisplay"
          class="h-8 w-8 rounded-full object-cover" />
        <span
          v-else
          class="h-8 w-8 rounded-full bg-grey-700 text-white text-sm font-semibold flex items-center justify-center">
          {{ authorDisplay[0] }}
        </span>
        <span class="text-white font-medium">{{ authorDisplay }}</span>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          @click="download">
          <span class="ga-icon ga-download text-xl" />
        </button>
        <button
          class="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          @click="$emit('close')">
          <span class="ga-icon ga-supprimer text-xl" />
        </button>
      </div>
    </div>

    <div class="flex-1 flex items-center justify-center min-h-0" @click.self="$emit('close')">
      <video
        ref="video"
        :src="src"
        class="max-h-full max-w-full"
        @play="onPlay"
        @pause="onPause"
        @loadedmetadata="onLoadedMetadata"
        @timeupdate="onTimeUpdate"
        @click="togglePlay" />
    </div>

    <div class="flex items-center gap-3 px-4 py-3 bg-grey-700 rounded-md mx-4 my-4" @click.stop>
      <button class="text-white" @click="togglePlay">
        <svg v-if="isPlaying" viewBox="0 0 24 24" class="h-5 w-5 fill-white">
          <rect x="6" y="4" width="4" height="16" />
          <rect x="14" y="4" width="4" height="16" />
        </svg>
        <svg v-else viewBox="0 0 24 24" class="h-5 w-5 fill-white"><path d="M6 4l15 8-15 8z" /></svg>
      </button>
      <button class="text-white" @click="skip(-10)">
        <span class="ga-icon ga-skip-backward text-lg" />
      </button>
      <button class="text-white" @click="skip(10)">
        <span class="ga-icon ga-skip-forward text-lg" />
      </button>
      <span class="text-white text-sm font-medium select-none w-10">{{ formattedCurrentTime }}</span>
      <div
        ref="seekBar"
        class="relative flex-1 min-w-0 h-1 rounded-full bg-white/30 cursor-pointer"
        @mousedown="startDrag">
        <div class="h-full rounded-full bg-secondary" :style="{width: progressPercent + '%'}" />
        <div
          class="absolute top-1/2 h-3 w-3 rounded-full bg-white -translate-y-1/2 -translate-x-1/2"
          :style="{left: progressPercent + '%'}" />
      </div>
      <span class="text-white text-sm font-medium w-10">{{ formattedDuration }}</span>
      <button class="text-white shrink-0" @click="toggleMute">
        <svg v-if="isMuted" viewBox="0 0 24 24" class="h-5 w-5 fill-none stroke-white" stroke-width="2">
          <path d="M4 9v6h4l5 4V5L8 9H4z" fill="white" stroke="none" />
          <path d="M17 9l5 6M22 9l-5 6" />
        </svg>
        <svg v-else viewBox="0 0 24 24" class="h-5 w-5 fill-none stroke-white" stroke-width="2">
          <path d="M4 9v6h4l5 4V5L8 9H4z" fill="white" stroke="none" />
          <path d="M16 8a5 5 0 0 1 0 8M19 5a9 9 0 0 1 0 14" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped></style>
